/**
 * Admin Notes tile (CrewDetailPage) — private notes admins/owners keep about
 * a crew member (abilities, reminders). Add form on top, newest-first list
 * below with author + timestamp. Delete is owner-only (server-enforced;
 * the button simply hides for admins). The crew member never sees these.
 */

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useOrganization } from '../../context/OrganizationContext';
import { listCrewNotes, createCrewNote, deleteCrewNote, type CrewNote } from '../../api/crewNotes';
import { getErrorMessage } from '../../api/client';

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface CrewNotesSectionProps {
  userId: string;
}

export default function CrewNotesSection({ userId }: CrewNotesSectionProps) {
  const { isOrgOwner } = useOrganization();
  const [notes, setNotes] = useState<CrewNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<CrewNote | null>(null);

  const refresh = useCallback(() => {
    listCrewNotes(userId)
      .then(setNotes)
      .catch(() => {
        // Non-fatal: the list just stays empty.
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await createCrewNote(userId, draft.trim());
      toast.success('Note added');
      setDraft('');
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await deleteCrewNote(userId, deleting.id);
      toast.success('Note deleted');
      setDeleting(null);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900">Admin Notes</h2>
      <p className="text-xs text-gray-500 mb-4">
        Visible to admins and owners only, never to the crew member.
      </p>

      <form onSubmit={handleAdd} className="mb-6">
        <label htmlFor="crew-note-draft" className="sr-only">
          Add a note
        </label>
        <textarea
          id="crew-note-draft"
          rows={2}
          maxLength={2000}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note: abilities, reminders, anything future-you should know…"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Adding…' : 'Add Note'}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="space-y-3">
          <div className="h-4 bg-gray-100 rounded animate-pulse" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-2/3" />
        </div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-gray-500">No notes yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {notes.map((note) => (
            <li key={note.id} className="py-3 first:pt-0 last:pb-0 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{note.body}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {note.author.firstName} {note.author.lastName} · {formatTimestamp(note.createdAt)}
                </p>
              </div>
              {isOrgOwner && (
                <button
                  type="button"
                  onClick={() => setDeleting(note)}
                  className="shrink-0 p-1 text-gray-400 hover:text-red-600"
                  aria-label="Delete note"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete this note?"
        message="The note will be permanently removed."
        confirmLabel="Delete note"
        isLoading={busy}
        variant="danger"
      />
    </div>
  );
}
